import React, { useEffect, useMemo, useState } from 'react';
import Form from 'react-bootstrap/Form';
import classnames from 'classnames';
import { FaHandshake, FaReceipt, FaSitemap } from 'react-icons/fa';
import { toast } from '../../toasts';
import { getError } from '../../utils';
import AttributionService from '../../services/attribution.service';
import {
	EntityList,
	PageHead,
	StatStrip,
	DataTable,
	Section,
	FormField,
	Select,
	EmptyState,
	TabNav,
	Pill,
	Button,
	controlClass,
} from '../../components/EntityList';
import {
	defaultRange,
	entityStats,
	flattenEntityRows,
	formatDay,
	formatMoney,
	formatMonth,
	formatPercent,
	partnerHeading,
	partnerRows,
	partnerSideStats,
	partnerStats,
	partnerStatusOptions,
	rangeError,
	sharedCostRows,
} from './attributionReportRows';

const QUICK_STATUS = 'cobrado-sin-liquidar';

function MoneyCell({ row, field, date }) {
	const amount = row[field];
	if (!amount) {
		return <span className='tw:text-ink-3'>—</span>;
	}
	return (
		<span className='tw:inline-flex tw:flex-col tw:items-end'>
			<span className={classnames('tw:font-mono tw:text-[13px]', amount < 0 && 'tw:text-danger')}>
				{formatMoney(row.currency, amount)}
			</span>
			{date && row[date] && <span className='tw:text-[11.5px] tw:text-ink-3'>{formatDay(row[date])}</span>}
		</span>
	);
}

const moneyColumn = (key, header, date) => ({
	key,
	header,
	className: 'tw:text-right',
	render: (row) => <MoneyCell row={row} field={key} date={date} />,
});

const UNIT_COLUMNS = [
	{
		key: 'centerName',
		header: 'Unidad',
		render: (row) => (
			<span className={classnames(row.isTotal ? 'tw:font-semibold tw:text-ink-0' : 'tw:text-ink-0', !row.centerId && !row.isTotal && 'tw:text-ink-2')}>
				{row.centerName}
			</span>
		),
	},
	{ key: 'currency', header: 'Moneda', className: 'tw:font-mono' },
	moneyColumn('facturado', 'Facturado'),
	moneyColumn('cobrado', 'Cobrado'),
	moneyColumn('gastos', 'Gastos'),
	moneyColumn('pagado', 'Pagado'),
	moneyColumn('resultado', 'Resultado'),
];

const PARTNER_COLUMNS = [
	{ key: 'month', header: 'Mes', className: 'tw:font-mono', render: (row) => formatMonth(row.month) },
	{ key: 'client', header: 'Cliente', render: (row) => <span className='tw:text-ink-0'>{row.client || '—'}</span> },
	{ key: 'currency', header: 'Moneda', className: 'tw:font-mono' },
	moneyColumn('facturado', 'Facturado'),
	moneyColumn('cobrado', 'Cobrado', 'cobradoEl'),
	moneyColumn('aLiquidar', 'A liquidar'),
	moneyColumn('liquidado', 'Liquidado', 'liquidadoEl'),
	{
		key: 'estado',
		header: 'Estado',
		render: (row) => (
			<Pill tone={row.status.tone} dot>
				{row.status.label}
			</Pill>
		),
	},
];

const SHARED_COST_COLUMNS = [
	{
		key: 'label',
		header: 'Concepto',
		render: (row) => (
			<span className={classnames(row.isTotal ? 'tw:font-semibold tw:text-ink-0' : 'tw:text-ink-0', row.kind === 'empresa' && 'tw:text-ink-2')}>
				{row.label}
			</span>
		),
	},
	{ key: 'currency', header: 'Moneda', className: 'tw:font-mono' },
	moneyColumn('amount', 'Monto'),
	{
		key: 'pct',
		header: '% del total',
		className: 'tw:text-right tw:font-mono',
		render: (row) => formatPercent(row.pct),
	},
];

const TAB_PREFIX = 'units';

/**
 * One partner unit: settlement strips per currency (period figures plus where
 * the settlement stands today), the costs charged to the partner and the
 * money in their favor, then the month × client rows with an Estado filter.
 */
function PartnerCard({ entry, loading }) {
	const [estado, setEstado] = useState('');
	const strips = useMemo(() => partnerStats(entry), [entry]);
	const sideStats = useMemo(() => partnerSideStats(entry), [entry]);
	const options = useMemo(() => partnerStatusOptions(entry), [entry]);
	const rows = useMemo(() => partnerRows(entry, estado), [entry, estado]);
	const selectId = `partner-status-${entry.unit?.id}`;
	const quickActive = estado === QUICK_STATUS;

	return (
		<section className='tw:mb-8'>
			<h2 className='tw:mb-3 tw:text-[15px] tw:font-semibold tw:tracking-[-0.01em] tw:text-ink-0'>{partnerHeading(entry)}</h2>
			{strips.map((strip) => (
				<div key={strip.currency} className='tw:mb-3'>
					<StatStrip stats={strip.stats} loading={loading} skeletonCount={strip.stats.length} />
				</div>
			))}
			{sideStats.length > 0 && (
				<div className='tw:mb-3'>
					<StatStrip stats={sideStats} loading={loading} skeletonCount={sideStats.length} />
				</div>
			)}
			<div className='tw:mb-3 tw:flex tw:flex-wrap tw:items-end tw:gap-2.5'>
				<FormField id={selectId} label='Estado' className='tw:min-w-[260px]'>
					<Select id={selectId} options={options} value={estado} onChange={(e) => setEstado(e.target.value)} />
				</FormField>
				<button
					type='button'
					onClick={() => setEstado(quickActive ? '' : QUICK_STATUS)}
					aria-pressed={quickActive}
					className={classnames(
						'tw:h-[34px] tw:rounded-[7px] tw:border tw:px-3 tw:text-[12.5px] tw:font-medium',
						quickActive
							? 'tw:border-accent tw:bg-accent-soft tw:text-accent'
							: 'tw:border-border tw:bg-surface tw:text-ink-2 tw:hover:bg-surface-2',
					)}
				>
					Cobrado, falta liquidar
				</button>
				<span className='tw:ml-auto tw:self-center tw:text-[12.5px] tw:text-ink-3'>
					{rows.length} {rows.length === 1 ? 'fila' : 'filas'}
				</span>
			</div>
			<DataTable
				columns={PARTNER_COLUMNS}
				rows={rows}
				loading={loading}
				rowKey={(row) => row.key}
				emptyMessage={estado ? 'Ninguna fila con ese estado en el período.' : 'Sin cobros ni liquidaciones de sus clientes en el período.'}
			/>
		</section>
	);
}

/**
 * Reportes → Unidades de negocio (SHOW_ATTRIBUTION, admin). Three tabs over
 * the same GET /attribution/report:
 *   - Resultado por unidad: per own entity, a StatStrip with the resultado per
 *     currency and one row per (unit, currency) plus the entity total rows
 *     ("General" = the entity's unassigned part).
 *   - Socios: one card per unit with a partner — what their clients were
 *     invoiced, what was collected and what was liquidated to the partner.
 *   - Costos compartidos: each entity's shared costs (bills) split between
 *     the partners (bill payers) and the company itself.
 * Currencies are never added together — the API keeps them apart and
 * attributionReportRows.js preserves that.
 */
function AttributionReport() {
	const [filters, setFilters] = useState(() => ({ ...defaultRange(), entity: '' }));
	const [entities, setEntities] = useState([]);
	const [report, setReport] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [tab, setTab] = useState('resultado');

	const dateError = rangeError(filters);

	useEffect(() => {
		let active = true;
		AttributionService.listEntities()
			.then(({ data }) => {
				if (active) setEntities(data.entities || []);
			})
			.catch(() => {});
		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		if (dateError) return undefined;
		let active = true;
		setLoading(true);
		setError('');
		AttributionService.getReport({ from: filters.from || undefined, to: filters.to, entity: filters.entity || undefined })
			.then(({ data }) => {
				if (active) setReport(data);
			})
			.catch((err) => {
				if (!active) return;
				const message = getError(err);
				setError(message);
				toast.error(message);
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [filters.from, filters.to, filters.entity, dateError]);

	const patchFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

	const entityOptions = useMemo(() => entities.map((e) => ({ value: String(e._id), label: e.name })), [entities]);

	const unattributed = report?.unattributed || {};
	const unattributedTotal = Number(unattributed.dues || 0) + Number(unattributed.creditDues || 0);
	const reportEntities = report?.entities || [];
	const partners = report?.partners || [];
	const sharedCosts = report?.sharedCosts || [];
	const noEntities = !loading && report && reportEntities.length === 0;
	const yearStart = defaultRange().from;

	const tabs = [
		{ id: 'resultado', label: 'Resultado por unidad', icon: <FaSitemap size={13} /> },
		{ id: 'socios', label: 'Socios', icon: <FaHandshake size={13} />, count: report ? partners.length : undefined, countLabel: 'socios' },
		{ id: 'costos', label: 'Costos compartidos', icon: <FaReceipt size={13} /> },
	];

	const noEntitiesState = (
		<div className='tw:rounded-panel tw:border tw:border-border'>
			<EmptyState
				icon={<FaSitemap />}
				title='Sin entidades propias'
				description='Marcá cuentas como entidad propia desde Configuración para ver el resultado de sus unidades de negocio acá.'
			/>
		</div>
	);

	return (
		<EntityList>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: 'Reportes', to: '/AdminScreen/reports' },
					{ label: 'Unidades de negocio' },
				]}
				title={
					<span className='tw:inline-flex tw:items-center tw:gap-3'>
						<span className='tw:grid tw:h-[38px] tw:w-[38px] tw:place-items-center tw:rounded-[10px] tw:bg-accent-soft tw:text-accent-ink'>
							<FaSitemap size={18} />
						</span>
						Unidades de negocio
					</span>
				}
				subtitle='Resultado de cada entidad propia por unidad de negocio, la liquidación de cada socio y cómo se reparten los costos compartidos. Siempre por moneda.'
			/>

			<Section title='Período y entidad' className='tw:mb-6'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 tw:min-[640px]:grid-cols-3'>
					<FormField id='attribution-from' label='Desde'>
						<Form.Control
							id='attribution-from'
							type='date'
							className={controlClass}
							value={filters.from}
							onChange={(e) => patchFilters({ from: e.target.value })}
						/>
					</FormField>
					<FormField id='attribution-to' label='Hasta'>
						<Form.Control
							id='attribution-to'
							type='date'
							className={controlClass}
							value={filters.to}
							onChange={(e) => patchFilters({ to: e.target.value })}
						/>
					</FormField>
					<FormField id='attribution-entity' label='Entidad'>
						<Select
							id='attribution-entity'
							options={entityOptions}
							value={filters.entity}
							onChange={(e) => patchFilters({ entity: e.target.value })}
							placeholder='Todas las entidades'
						/>
					</FormField>
				</div>
				<div className='tw:mt-3 tw:flex tw:flex-wrap tw:items-center tw:gap-2'>
					<Button size='sm' variant={filters.from === yearStart ? 'default' : 'ghost'} onClick={() => patchFilters(defaultRange())}>
						Año en curso
					</Button>
					<Button size='sm' variant={filters.from ? 'ghost' : 'default'} onClick={() => patchFilters({ from: '' })}>
						Todo el historial
					</Button>
					{!filters.from && <span className='tw:text-[12.5px] tw:text-ink-3'>Sin fecha de inicio: todo hasta la fecha Hasta.</span>}
				</div>
				{dateError && <p className='tw:mb-0 tw:mt-3 tw:text-[12.5px] tw:text-danger'>{dateError}</p>}
				{!dateError && unattributedTotal > 0 && (
					<p className='tw:mb-0 tw:mt-3 tw:text-[12.5px] tw:text-ink-2'>
						Sin atribuir en el período: {unattributed.dues || 0} cuota(s) de deudas y {unattributed.creditDues || 0} cuota(s) de pasividades
						no están asignadas a ninguna entidad.
					</p>
				)}
			</Section>

			<div className='tw:mb-5'>
				<TabNav tabs={tabs} current={tab} onChange={setTab} idPrefix={TAB_PREFIX} />
			</div>

			{loading && !report && <StatStrip stats={[]} loading skeletonCount={3} />}

			{tab === 'resultado' && (
				<div role='tabpanel' id={`${TAB_PREFIX}panel-resultado`} aria-labelledby={`${TAB_PREFIX}-resultado`}>
					{noEntities && noEntitiesState}
					{reportEntities.map((entity) => {
						const stats = entityStats(entity);
						const rows = flattenEntityRows(entity);
						return (
							<section key={entity.id} className='tw:mb-6'>
								<h2 className='tw:mb-3 tw:text-[15px] tw:font-semibold tw:tracking-[-0.01em] tw:text-ink-0'>{entity.name}</h2>
								{stats.length > 0 && (
									<div className='tw:mb-3'>
										<StatStrip stats={stats} loading={loading} skeletonCount={stats.length} />
									</div>
								)}
								<DataTable
									columns={UNIT_COLUMNS}
									rows={rows}
									loading={loading}
									error={error}
									rowKey={(row) => row.key}
									rowClassName={(row) => (row.isTotal ? 'tw:bg-surface-2 tw:font-semibold' : undefined)}
									emptyMessage='Sin movimientos atribuidos a esta entidad en el período.'
								/>
							</section>
						);
					})}
				</div>
			)}

			{tab === 'socios' && (
				<div role='tabpanel' id={`${TAB_PREFIX}panel-socios`} aria-labelledby={`${TAB_PREFIX}-socios`}>
					{noEntities && noEntitiesState}
					{!noEntities && report && !loading && partners.length === 0 && (
						<div className='tw:rounded-panel tw:border tw:border-border'>
							<EmptyState
								icon={<FaHandshake />}
								title='Ninguna unidad tiene socio'
								description='Asigná un socio a una unidad de negocio desde Configuración para seguir lo que cobran sus clientes y lo que se le liquida.'
							/>
						</div>
					)}
					{partners.map((entry) => (
						<PartnerCard key={entry.unit?.id} entry={entry} loading={loading} />
					))}
				</div>
			)}

			{tab === 'costos' && (
				<div role='tabpanel' id={`${TAB_PREFIX}panel-costos`} aria-labelledby={`${TAB_PREFIX}-costos`}>
					<p className='tw:mb-4 tw:text-[12.5px] tw:text-ink-2'>
						Gastos de la entidad (impuestos, BPS, contador, servicios) y cuánto le corresponde a cada socio según los pagadores de cada gasto.
					</p>
					{noEntities && noEntitiesState}
					{!noEntities && report && !loading && sharedCosts.length === 0 && (
						<div className='tw:rounded-panel tw:border tw:border-border'>
							<EmptyState icon={<FaReceipt />} title='Sin gastos en el período' description='Ninguna entidad propia tiene gastos en las fechas elegidas.' />
						</div>
					)}
					{sharedCosts.map((entry) => (
						<section key={entry.entity.id} className='tw:mb-6'>
							<h2 className='tw:mb-3 tw:text-[15px] tw:font-semibold tw:tracking-[-0.01em] tw:text-ink-0'>{entry.entity.name}</h2>
							<DataTable
								columns={SHARED_COST_COLUMNS}
								rows={sharedCostRows(entry)}
								loading={loading}
								error={error}
								rowKey={(row) => row.key}
								rowClassName={(row) => (row.isTotal ? 'tw:bg-surface-2 tw:font-semibold' : undefined)}
								emptyMessage='Sin gastos en el período.'
							/>
						</section>
					))}
				</div>
			)}
		</EntityList>
	);
}

export default AttributionReport;
